import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import ChatModal from './ChatModal';
import { ScrollArea } from '../ui/scroll-area';

interface AppLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  breadcrumb?: string;
  className?: string;
  onUploadClick?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  pageTitle,
  breadcrumb,
  className = '',
  onUploadClick
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close mobile sidebar when screen becomes desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile]);

  const handleChatOpen = () => {
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleMobileSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  return (
    <div className={`flex flex-col h-screen bg-gray-50 ${className}`}>
      {/* Top Header - spans full width */}
      <TopHeader 
        pageTitle={pageTitle}
        breadcrumb={breadcrumb}
        onChatOpen={handleChatOpen}
        onMobileSidebarToggle={handleMobileSidebarToggle}
        isMobile={isMobile}
      />
      
      {/* Main layout with sidebar and content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Desktop Sidebar */}
        {!isMobile && <Sidebar onUploadClick={onUploadClick} />}

        {/* Mobile Sidebar Overlay */}
        {isMobile && (
          <>
            {/* Backdrop */}
            {isMobileSidebarOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={handleMobileSidebarClose}
              />
            )}

            {/* Mobile Sidebar */}
            <div className={`fixed top-0 left-0 h-full z-50 transform transition-transform duration-300 ease-in-out ${
              isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
              <Sidebar onMobileClose={handleMobileSidebarClose} isMobile={true} onUploadClick={onUploadClick} />
            </div>
          </>
        )}
        
        {/* Page content */}
        <ScrollArea className="flex-1">
          <main className="p-2">
            {children}
          </main>
        </ScrollArea>
      </div>
      
      {/* Chat Modal */}
      <ChatModal 
        isOpen={isChatOpen} 
        onClose={handleChatClose} 
      />
    </div>
  );
};

export default AppLayout;
